import { useEffect, useRef, useState } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '../../lib/utils';
import { parseFlexibleDate } from '../../lib/dateInput';
import { DatePopover } from './DatePopover';

/**
 * Hybrid date field: a text input that accepts pasted/typed dates in a
 * few common formats (handled by `parseFlexibleDate`), plus a calendar
 * icon that pops the custom Asana-style {@link DatePopover}. Single
 * source of truth for date entry across the app — replaces every
 * `<input type="date">` we used to scatter through forms.
 *
 * The popover is portal'd to body and dismissed via outside-click
 * detection that considers BOTH the trigger and the popover "inside".
 * The native `<input type="date">` overlay was avoided because it
 * steals focus when the user clicks the picker's month-nav arrows,
 * collapsing the editor mid-pick — and because typing/pasting into the
 * native segment-based input is a UX dead-end.
 *
 * Commit semantics:
 *   - Picker selection → onChange immediately, popover closes.
 *   - Text blur with parseable input → onChange with the canonical
 *     `YYYY-MM-DD` form.
 *   - Text blur with garbage → silently revert to the last known good
 *     value. We don't dirty the form for a typo.
 *   - Empty text → onChange('').
 */
export interface DateInputProps {
  /** Canonical `YYYY-MM-DD` string, or `''` for "unset". */
  value: string;
  /** Fires on every committed change with the new canonical value. */
  onChange: (next: string) => void;
  /** Greys the field and blocks all interaction. */
  disabled?: boolean;
  /** Red border (e.g. when a parent validation flagged the value). */
  invalid?: boolean;
  /** Optional className applied to the outer trigger box. */
  className?: string;
  /** Optional placeholder; defaults to `YYYY-MM-DD`. */
  placeholder?: string;
  /** Optional aria-label for the inner text input — required where the
   * field has no associated visible <label>, e.g. compact table cells. */
  ariaLabel?: string;
  /** Earliest allowed value, inclusive, `YYYY-MM-DD`. Used by both the
   *  popover (greys out-of-range days) and the text path (rejects parsed
   *  values below this on blur). Lex-compared. */
  min?: string;
  /** Latest allowed value, inclusive, `YYYY-MM-DD`. */
  max?: string;
}

export function DateInput({
  value,
  onChange,
  disabled,
  invalid,
  className,
  placeholder,
  ariaLabel,
  min,
  max,
}: DateInputProps) {
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Keep the text in sync when the source value changes externally
  // (form reset, item switch, server round-trip, etc.).
  useEffect(() => {
    setText(value);
  }, [value]);

  const commit = (raw: string) => {
    const parsed = parseFlexibleDate(raw);
    if (parsed === null) {
      // Unparseable — revert. We never persist a half-typed string,
      // and we don't want to dirty the form for a typo.
      setText(value);
      return;
    }
    if (
      (min !== undefined && min !== '' && parsed < min) ||
      (max !== undefined && max !== '' && parsed > max)
    ) {
      setText(value);
      return;
    }
    if (parsed !== value) onChange(parsed);
    setText(parsed);
  };

  // Close the popover when the click lands outside both the trigger
  // and the portal'd calendar. Esc dismisses too.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Defer the listener install one tick so the click that opened
    // the popover doesn't immediately close it.
    const tid = window.setTimeout(() => {
      document.addEventListener('mousedown', onDown);
      window.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <div
        ref={triggerRef}
        className={cn(
          'flex w-full items-center gap-1 rounded-md border bg-white px-2 py-1.5 text-sm shadow-sm focus-within:border-sky-500 focus-within:ring-2 focus-within:ring-sky-200',
          disabled
            ? 'cursor-not-allowed bg-slate-50 opacity-70'
            : 'border-slate-300',
          invalid ? '!border-red-500 focus-within:!ring-red-200' : '',
          className,
        )}
      >
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={text}
          placeholder={placeholder ?? 'YYYY-MM-DD'}
          aria-label={ariaLabel}
          onChange={(e) => setText(e.target.value)}
          onBlur={(e) => commit(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          className="w-full min-w-0 bg-transparent outline-none disabled:cursor-not-allowed"
        />
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          // mousedown (not click) + preventDefault: keep focus where it
          // is so the text input doesn't blur and trigger a stale
          // commit before the popover toggles.
          onMouseDown={(e) => {
            e.preventDefault();
            setOpen((v) => !v);
          }}
          className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="カレンダーから選択"
          title="カレンダーから選択"
        >
          <Calendar className="h-3.5 w-3.5" />
        </button>
      </div>
      {open && !disabled ? (
        <DatePopover
          selected={parseFlexibleDate(text) || value || null}
          anchorEl={triggerRef.current}
          popoverRef={popoverRef}
          min={min}
          max={max}
          onPick={(picked) => {
            setText(picked);
            if (picked !== value) onChange(picked);
            setOpen(false);
          }}
          onClear={() => {
            setText('');
            if (value !== '') onChange('');
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
